import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input, ViewChild, Output, EventEmitter, TemplateRef } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { AuthService, Project, Note, NoteAttachment, Tags, AlertifyService, UserService, ClientContactService, ClientService, PartnerContactService, ProjectDataService, ProjectService, Reaction } from 'core';
import { NotesService } from 'projects/core/src/services/note.service';
import { UploaderService } from 'projects/core/src/services/uploader.service';
import { v4 as uuidv4 } from 'uuid';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { catchError, forkJoin, map, Observable, of, Subject, Subscription, switchMap, take, takeUntil, tap } from 'rxjs';

type NoteModalEditorMode = 'compose' | 'editNote' | 'editReply' | 'addReply';

interface NoteModalTarget {
  id: string;
  message: string;
  userType?: string;
  attachments?: NoteAttachment[];
}

// Note attachments: any file type up to 500 MB, uploaded straight to S3 under a
// per-upload folder so two files with the same name never overwrite each other
const MAX_ATTACHMENT_BYTES = 500 * 1024 * 1024;
const NOTE_ATTACHMENT_FOLDER = 'asset/note-attachments';
// Draft attachments are keyed by note id; a note being composed has no id yet
const NEW_NOTE_DRAFT = 'new';

interface AttachmentUpload {
  id: string;
  fileName: string;
  progress: number;
  subscription?: Subscription;
}

interface AttachmentLinkDraft {
  name: string;
  url: string;
}

interface PresignedUpload {
  uploadUrl: string;
  url: string;
}

interface NoteModalState {
  editorMode: NoteModalEditorMode | null;
  readOnly: boolean;
  target: NoteModalTarget | null;
  isReply: boolean;
  canEdit: boolean;
  title: string;
}

@Component({
    selector: 'app-notes',
    templateUrl: './notes.component.html',
    styleUrls: ['./notes.component.scss'],
    standalone: false
})
export class NotesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = true;
  notes: any[] = [];
  currentNoteTooltip: any;
  tags: Tags[] = [];
  users: any[] = [];
  vendors: any[] = [];
  vendorsContacts: any[] = [];
  clientContacts: any[] = [];
  showAll = true;
  newNote: any = { userType: 'Team' };
  deleteNote: any = {};
  notesConfirmEdits = {};
  notesExpanded = {};
  addNoteModeOn = false;
  fullList: any[] = [];
  clientId: string = null;
  vendorId: string = null;
  isVendorOrClient = false;
  isVendor = false;
  isClient = false;
  private _resolvedClientName: string = null;
  private _resolvedClientId: string = null;
  get resolvedClientName(): string {
    if (this._resolvedClientId && this._resolvedClientId !== (this.clientId || this.project?.clientId)) {
      return this.project?.clientName || null;
    }
    return this._resolvedClientName || this.project?.clientName || null;
  }
  set resolvedClientName(value: string) {
    this._resolvedClientName = value;
    this._resolvedClientId = this.clientId || this.project?.clientId || null;
  }
  maxChars = 300;
  myId: string = null;
  reactionTypes = ['thumbs-up', 'confirmed', 'happy', 'reviewing', 'agree', 'important', 'on-target'];

  filtersApplied = [];
  filterValues: any[] = [{ label: 'Show All', value: 'showAll', selected: true }];

  showContactPicker = false;
  contactPickerMode: 'client' | 'vendor' = 'client';
  contactPickerSearch = '';
  pickerContacts: any[] = [];
  selectedPickerContact: any = null;
  pickerVendorId: string = null;
  showAddContactForm = false;
  newPickerContact: any = {};
  contactPickerSaving = false;

  openModalRef: any;
  @ViewChild('modalDelete') modalDelete: BsModalRef;
  @ViewChild('modalNote') modalNote: TemplateRef<unknown>;

  noteModal: NoteModalState | null = null;

  readonly newNoteDraft = NEW_NOTE_DRAFT;
  draftAttachments: Record<string, NoteAttachment[]> = {};
  // Saved attachments still kept on a note being edited, keyed by note id
  keptAttachments: Record<string, NoteAttachment[]> = {};
  attachmentUploads: Record<string, AttachmentUpload[]> = {};
  linkDrafts: Record<string, AttachmentLinkDraft> = {};

  editorConfig = {
    placeholder: 'Your Note',
    startupFocus: true,
    toolbar: {
      language: 'en',
      items: ['bold', 'italic', 'underline', 'link', 'bulletedList', 'numberedList']
    },
    link: { defaultProtocol: 'https://', addTargetToExternalLinks: true }
  };

  editorReplyConfig = { ...this.editorConfig, placeholder: 'Type your reply here...' };

  @Input() project = {} as Project;
  @Input() bidNumber = '' as string;
  @Output() closeCallback = new EventEmitter();

  constructor(
    private noteService: NotesService,
    public auth: AuthService,
    private modalService: BsModalService,
    private alertify: AlertifyService,
    private projectDataService: ProjectDataService,
    private projectService: ProjectService,
    private userService: UserService,
    private partnerContactService: PartnerContactService,
    private clientContactService: ClientContactService,
    private clientService: ClientService,
    private uploader: UploaderService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.myId = this.auth.getUser().id;

    forkJoin([
      this.projectDataService.getClientViewSidebar.pipe(take(1)),
      this.projectDataService.getVendorViewSidebar.pipe(take(1))
    ]).pipe(
      tap(([clientData, vendorData]) => {
        this.isClient = this.auth.isClient() || !!clientData;
        this.isVendor = this.auth.isVendor() || !!vendorData;
        this.isVendorOrClient = this.isClient || this.isVendor;
        this.clientId = clientData || null;
        this.vendorId = vendorData || null;

        if (this.isClient && !this.clientId) this.clientId = this.auth.getUser().clientId;
        if (this.isVendor && !this.vendorId) this.vendorId = this.auth.getUser().vendorId;
      }),
      switchMap(() => {
        if (this.bidNumber) return this.getNotes();

        this.filterValues = [
          { label: 'Client', value: 'client' },
          { label: 'Vendor', value: 'vendor' },
          { label: 'Team', value: 'team' }
        ];

        if (this.clientId) {
          this.newNote.userType = 'Client';
          this.newNote.clientId = this.clientId;
          return this.loadClientContacts().pipe(switchMap(() => this.getClientNotes()));
        }

        if (this.vendorId) {
          this.newNote.userType = 'Vendor';
          this.newNote.partnerId = this.vendorId;
          return this.loadVendors().pipe(switchMap(() => this.getVendorNotes()));
        }

        return forkJoin([this.loadUsers(), this.loadClientContacts(), this.loadVendors()]).pipe(
          switchMap(() => this.getNotes())
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({ error: error => console.error(error) });
  }

  ngOnDestroy() {
    Object.keys(this.attachmentUploads).forEach(key => this.clearDraftAttachments(key));
    this.destroy$.next();
    this.destroy$.complete();
  }

  receiveMessage($event) { this.newNote.message = $event; }
  getVendors = () => {
    const selectedIds = new Set((this.newNote?.vendors ?? []).map(v => v.id));
    return of(this.vendors.filter(v => !selectedIds.has(v.id)));
  };
  get allVendorsAdded(): boolean {
    return this.vendors.length > 0 && (this.newNote?.vendors?.length ?? 0) >= this.vendors.length;
  }
  getNotesConfirmEditsCount() { return Object.keys(this.notesConfirmEdits).length; }
  isAnyEditActive(): boolean {
    return Object.values(this.notesConfirmEdits as Record<string, any>).some((s: any) => s?.editModeOn || s?.addReplyModeOn);
  }
  hasActiveReplyEdit(note: any): boolean {
    return (note?.replies ?? []).some((r: any) => this.notesConfirmEdits[r.id]?.editModeOn);
  }

  private toUtcDate(value: string): string {
    if (!value || typeof value !== 'string') return value;
    return !value.endsWith('Z') && !/[+\-]\d{2}:\d{2}$/.test(value) ? value + 'Z' : value;
  }

  private mapNote(note: any): any {
    note.cDate = this.toUtcDate(note.cDate);
    note.replies = note.replies
      .map(r => ({ ...r, createdOn: this.toUtcDate(r.createdOn) }))
      .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());

    let taggedUsers = [];
    if (note.userType === 'Vendor') {
      taggedUsers = this.vendorsContacts.filter(u => note.taggedUsers?.includes(u.id)).map(u => ({ id: u.id, name: u.name }));
    } else if (note.userType === 'Client') {
      taggedUsers = this.clientContacts.filter(u => note.taggedUsers?.includes(u.id)).map(u => ({ id: u.id, name: u.name }));
    } else {
      taggedUsers = this.users.filter(u => note.taggedUsers?.includes(u.id)).map(u => ({ id: u.id, name: u.name }));
    }

    return { ...note, users: taggedUsers, usersDisplay: taggedUsers.map(u => u.name) };
  }

  private refreshNotes() {
    if (this.clientId) this.getClientNotes().subscribe();
    else if (this.vendorId) this.getVendorNotes().subscribe();
    else this.getNotes().subscribe();
  }

  getNotes(): Observable<any> {
    if (this.bidNumber) {
      return this.noteService.getProjectNotesForSummary(this.bidNumber).pipe(
        tap(result => {
          this.notes = result.map(note => this.mapNote(note));

          const distinctProjects = [...new Set(this.notes.map((n: any) => n.projectId))];
          distinctProjects.forEach(projectId => {
            const projectCode = this.notes.find((n: any) => n.projectId === projectId)?.projectCode;
            this.filterValues.push({ label: projectCode, value: projectId, group: 'Project Number' });
          });

          this.fullList = this.notes;
          this.cdr.detectChanges();
          this.loading = false;
        }),
        catchError(error => { console.error(error); this.loading = false; return of([]); })
      );
    }

    return this.noteService.getProjectNotes(this.project.id).pipe(
      tap(result => {
        this.notes = result
          .filter(e => e.projectId === this.project.id)
          .map(note => {
            const partnerName = this.project.projectSegments[0].projectSurveyPartners
              .find(x => x.partnerId === note.partnerId)?.partnerName;
            note.partnerName = partnerName || '';
            return this.mapNote(note);
          });

        this.fullList = this.notes;
        this.cdr.detectChanges();
        this.loading = false;
      }),
      catchError(error => { console.error(error); this.loading = false; return of([]); })
    );
  }

  getClientNotes(): Observable<any> {
    return this.noteService.getClientNotesInProject(this.project.id, this.clientId).pipe(
      tap(result => {
        this.notes = result.map(note => this.mapNote(note));
        this.fullList = this.notes;
        this.cdr.detectChanges();
        this.loading = false;
      }),
      catchError(error => { console.error(error); this.loading = false; return of([]); })
    );
  }

  getVendorNotes(): Observable<any> {
    return this.noteService.getVendorNotesInProject(this.project.id, this.vendorId).pipe(
      tap(result => {
        this.notes = result.map(note => this.mapNote(note));
        this.fullList = this.notes;
        this.cdr.detectChanges();
        this.loading = false;
      }),
      catchError(error => { console.error(error); this.loading = false; return of([]); })
    );
  }

  getDisplayItems(note): string[] { return note.usersDisplay.slice(0, 1); }
  getRemainingCount(note): number { return note.usersDisplay.length - 1; }
  showPlusButton(note): boolean { return note.usersDisplay.length > 1; }

  loadUsers(): Observable<any> {
    return this.userService.GetUsers().pipe(
      tap(data => {
        const me = data.find(e => e.id === this.myId);
        this.users = me ? [me, ...data.filter(e => e.id !== this.myId)] : data;
      }),
      catchError(error => { console.error(error); return of([]); })
    );
  }

  loadClientContacts(): Observable<any> {
    this.clientContacts = [];
    const clientId = this.clientId || this.project.clientId;

    return forkJoin([
      this.clientContactService.GetClientContacts(clientId).pipe(catchError(() => of([]))),
      this.clientService.GetClient(clientId, true).pipe(catchError(() => of(null)))
    ]).pipe(
      tap(([data, client]: [any[], any]) => {
        this.resolvedClientName = client?.name || this.project.clientName || null;
        this.cdr.detectChanges();

        const contacts = data.map(e => ({ id: e.id, name: [e.firstName, e.lastName].filter(Boolean).join(' '), type: 'ClientContact', email: e.email }));

        if (this.isClient) {
          const projectTeam = this.project.projectTeam
            .filter(e => !e.projectRole.toLowerCase().includes('vendor'))
            .map(e => ({ id: e.userId, name: e.displayName, type: e.projectRole }));
          const uniqueMap = new Map([...contacts, ...projectTeam].map(item => [item.id, item]));
          this.clientContacts = Array.from(uniqueMap.values());
        } else {
          this.clientContacts = contacts;
        }
      })
    );
  }

  loadVendors(): Observable<any> {
    const projectVendors = this.project.projectSegments?.[0]?.projectSurveyPartners
      ?.filter(x => !x.isDeleted)
      ?.sort((a, b) => a.partnerName.localeCompare(b.partnerName)) ?? [];

    if (projectVendors.length === 0) {
      this.vendors = [];
      this.vendorsContacts = [];
      return of([]);
    }

    const vendorObservables = projectVendors.map(vendor =>
      this.partnerContactService.GetPartnerContacts(vendor.partnerId).pipe(
        map(results => ({
          id: vendor.partnerId,
          name: vendor.partnerName,
          contacts: results.map(c => ({ id: c.id, name: [c.firstName, c.lastName].filter(Boolean).join(' '), type: 'VendorContact', vendorId: vendor.partnerId, email: c.email }))
        })),
        catchError(() => of({ id: vendor.partnerId, name: vendor.partnerName, contacts: [] }))
      )
    );

    return forkJoin(vendorObservables).pipe(
      tap(result => {
        this.vendors = result;
        const allContacts = result.flatMap(v => v.contacts);

        if (this.isVendor) {
          const projectTeam = this.project.projectTeam.map(e => ({ id: e.userId, name: e.displayName, type: e.projectRole }));
          const uniqueMap = new Map([...allContacts, ...projectTeam].map(item => [item.id, item]));
          this.vendorsContacts = Array.from(uniqueMap.values());
        } else {
          this.vendorsContacts = allContacts;
        }
      })
    );
  }

  addNote() {
    if (this.bidNumber) return;

    this.newNote.message = this.textOnly(this.newNote.message);

    if (!this.newNote.message?.trim()) {
      this.alertify.error('Please write a note.');
      return;
    }

    if (this.newNote.userType === 'Vendor' && !this.clientId && !this.vendorId && !this.newNote.vendors?.length) {
      this.alertify.error('Please select at least one Vendor.');
      return;
    }

    if (this.isUploadingAttachments(NEW_NOTE_DRAFT)) {
      this.alertify.error('Please wait for your attachments to finish uploading.');
      return;
    }

    this.newNote.attachments = this.attachmentsFor(NEW_NOTE_DRAFT);

    this.newNote.id = uuidv4();
    this.newNote.projectId = this.project.id;
    this.newNote.tags = this.extractTags(this.newNote.message);
    this.newNote.taggedUsers = this.resolveTaggedUsers(this.newNote.userType, this.vendorId);

    const emailBlocked = this.newNote.userType === 'Team' && this.hasNoTeamMembers();
    const emailMsg = (this.newNote.sendAsEmail && !emailBlocked) ? ' Email sent.' : '';

    // Multi-vendor path: one note per vendor, each carrying the same attachments
    if (this.newNote.userType === 'Vendor' && !this.clientId && !this.vendorId) {
      const requests = (this.newNote.vendors || []).map(vendor =>
        this.noteService.addNote({
          ...this.newNote,
          id: uuidv4(),
          partnerId: vendor.id,
          clientId: null,
          taggedUsers: this.getVendorPmIds(vendor.id)
        })
      );

      forkJoin(requests).subscribe(() => {
        this.addNoteModeOn = false;
        this.clearDraftAttachments(NEW_NOTE_DRAFT);
        this.alertify.success(`Vendor notes added successfully.${emailMsg}`);
        this.resetNewNote();
        this.refreshNotes();
      }, error => console.error(error));
      return;
    }

    // Single note path
    this.noteService.addNote(this.newNote).subscribe(() => {
      this.addNoteModeOn = false;
      this.clearDraftAttachments(NEW_NOTE_DRAFT);
      this.alertify.success(`Note added successfully.${emailMsg}`);
      this.resetNewNote();
      this.refreshNotes();
    }, error => console.error(error));
  }

  updateNote(note) {
    if (this.isUploadingAttachments(note.id)) {
      this.alertify.error('Please wait for your attachments to finish uploading.');
      return;
    }

    note.message = this.notesConfirmEdits[note.id].message;
    note.taggedUsers = this.resolveTaggedUsers(note.userType, note.partnerId);

    // Only the attachments added in this edit are sent, plus the ids of saved ones the user removed
    const kept = this.keptAttachmentsFor(note.id);
    const removedAttachmentIds = (note.attachments ?? []).filter(a => !kept.some(k => k.id === a.id)).map(a => a.id);
    this.noteService.editNote({ ...note, attachments: this.attachmentsFor(note.id), removedAttachmentIds }).subscribe(() => {
      this.alertify.success('Note edited successfully');
      this.clearDraftAttachments(note.id);
      delete this.notesConfirmEdits[note.id];
      this.refreshNotes();
    }, error => { console.error(error); this.alertify.error('Unable to edit note'); });
  }

  delete() {
    this.noteService.deleteNote(this.deleteNote.id).subscribe(() => {
      this.alertify.success('Note deleted successfully');
      this.closeModal();
      this.refreshNotes();
    }, error => { console.error(error); this.alertify.error('Unable to delete note'); });
  }

  editNote(note) {
    this.notesConfirmEdits[note.id] = { editModeOn: true, message: note.message };
    this.keptAttachments[note.id] = [...(note.attachments ?? [])];
  }

  cancel(note?) {
    this.addNoteModeOn = false;
    this.resetNewNote();
    this.clearDraftAttachments(NEW_NOTE_DRAFT);
    if (note) {
      this.clearDraftAttachments(note.id);
      delete this.notesConfirmEdits[note.id];
    }
  }

  replyToNote(note) {
    if (!this.notesConfirmEdits[note.id]) {
      this.notesConfirmEdits[note.id] = { message: note.message };
    }
    this.notesConfirmEdits[note.id].addReplyModeOn = true;
  }

  saveReply(note) {
    const reply = {
      id: uuidv4(),
      noteId: note.id,
      message: this.notesConfirmEdits[note.id].replyMessage,
      projectId: this.project.id
    };

    if (!reply.message?.trim()) { this.alertify.error('Please write a reply.'); return; }

    this.noteService.addNoteReply(reply).subscribe(() => {
      this.alertify.success('Reply added successfully');
      delete this.notesConfirmEdits[note.id];
      this.refreshNotes();
    }, error => { console.error(error); this.alertify.error('Unable to add reply'); });
  }

  editReply(reply) {
    reply.message = this.notesConfirmEdits[reply.id].message;

    if (!reply.message?.trim()) { this.alertify.error('Please write a reply.'); return; }

    this.noteService.editNoteReply(reply).subscribe(() => {
      this.alertify.success('Reply edited successfully');
      delete this.notesConfirmEdits[reply.id];
      this.refreshNotes();
    }, error => { console.error(error); this.alertify.error('Unable to edit reply'); });
  }

  deleteReply(reply) {
    this.noteService.deleteNoteReply(reply.id).subscribe(() => {
      this.alertify.success('Reply deleted successfully');
      this.refreshNotes();
    }, error => { console.error(error); this.alertify.error('Unable to delete reply'); });
  }

  cancelReply(reply) { delete this.notesConfirmEdits[reply?.id]; }

  applyFilter(filters) {
    this.filtersApplied = filters?.filter(x => x.value !== 'showAll' && x?.selected).map(x => x.value);
    this.showAll = filters?.some(x => x.value === 'showAll' && x?.selected);
    this.bidNumber ? this.filterListSummary() : this.filterList();
  }

  filterList() {
    this.notes = this.filtersApplied.length > 0
      ? this.fullList.filter(item => this.filtersApplied.includes(item.userType?.toLowerCase()))
      : [...this.fullList];
  }

  filterListSummary() {
    this.notes = this.showAll
      ? [...this.fullList]
      : this.fullList.filter(item => this.filtersApplied.includes(item.projectId));
  }

  changeType(note, userType) {
    if (note.userType === userType) return;
    note.userType = userType;
    note.clientId = userType === 'Client' ? this.project?.clientId : null;
    note.vendors = [];
    note.users = [];
    this.showContactPicker = false;
    this.showAddContactForm = false;
    this.selectedPickerContact = null;
    this.pickerContacts = [];
    this.newPickerContact = {};

    if (userType === 'Client' && !this.resolvedClientName) {
      const clientId = this.clientId || this.project.clientId;
      this.clientService.GetClient(clientId, true).pipe(take(1)).subscribe(client => {
        this.resolvedClientName = client?.name || this.project.clientName || null;
        this.cdr.detectChanges();
      });
    }
  }

  updateNoteVendors(note, event) {
    note.vendors = event.map(e => ({ id: e.id, name: e.name }));

    // Rebuild vendorsContacts for selected vendors only
    const contactMap = new Map<string, any>();
    for (const vendor of note.vendors) {
      const vendorData = this.vendors.find(v => v.id === vendor.id);
      vendorData?.contacts?.forEach(c => { if (!contactMap.has(c.id)) contactMap.set(c.id, c); });
    }
    this.vendorsContacts = Array.from(contactMap.values());

    // Remove users no longer in available contacts
    const validIds = new Set(this.vendorsContacts.map(c => c.id));
    if (note.users) note.users = note.users.filter(u => validIds.has(u.id));

    if (this.showContactPicker && this.contactPickerMode === 'vendor') {
      this.refreshVendorPickerContacts();
    }
  }

  private getVendorPmIds(partnerId: string): string[] {
    return (this.project?.projectTeam || [])
      .filter(e => e.projectRole === 'Vendor PM' && e.partnerId?.toLowerCase() === partnerId?.toLowerCase())
      .map(e => e.userId);
  }

  getVendorNames(vendors: any[]): string {
    if (!vendors?.length) return '';
    const names = vendors.map(v => v.name);
    if (names.length === 1) return names[0];
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  getVendorBannerName(): string {
    if (this.vendorId) return this.vendors?.find(v => v.id === this.vendorId)?.name ?? '';
    return this.getVendorNames(this.newNote?.vendors);
  }

  get vendorsMissingContacts(): { id: string; name: string }[] {
    return (this.newNote?.vendors ?? []).filter(selected => this.getVendorPmIds(selected.id).length === 0);
  }

  anyVendorHasNoContacts(): boolean {
    return this.vendorsMissingContacts.length > 0;
  }

  hasNoTeamMembers(): boolean {
    return this.getTeamTaggedUsers().length === 0;
  }

  private resolveTaggedUsers(userType: string, vendorId?: string): string[] {
    if (userType === 'Client') {
      return this.project?.clientContactId ? [this.project.clientContactId] : [];
    }
    if (userType === 'Vendor') {
      return this.getVendorPmIds(vendorId);
    }
    return this.getTeamTaggedUsers();
  }

  private getTeamTaggedUsers(): string[] {
    const teamRoles = ['PM', 'Secondary PM', 'Programmer'];
    const ids = (this.project?.projectTeam || [])
      .filter(e => teamRoles.includes(e.projectRole))
      .map(e => e.userId);
    if (this.project?.internalAccountOwnerId) ids.push(this.project.internalAccountOwnerId);
    return [...new Set(ids)];
  }

  parseNote(content: string, noteId: any): string {
    if (!content) return '';
    const textOnly = content.replace(/<[^>]*>/g, '');
    if (textOnly.length <= this.maxChars) return content;

    const truncated = textOnly.substring(0, this.maxChars);
    let breakAt = truncated.lastIndexOf(' ');
    if (breakAt === -1 || breakAt < this.maxChars * 0.8) breakAt = this.maxChars;
    return textOnly.substring(0, breakAt) + ' . . .' + this.getViewMoreLink();
  }

  getViewMoreLink(): string {
    return `<span class="note-toggle-link nav-accent-blue font-weight-bold cursor-pointer ms-2 nav-font12">View more</span>`;
  }

  onNoteMessageClick(event: MouseEvent, note: any) {
    if ((event.target as HTMLElement).classList.contains('note-toggle-link')) {
      event.preventDefault();
      event.stopPropagation();
      // long notes open in the Expand modal rather than growing inline
      this.openNoteView(note);
    }
  }

  onReplyMessageClick(event: MouseEvent, reply: any) {
    if ((event.target as HTMLElement).classList.contains('note-toggle-link')) {
      event.preventDefault();
      event.stopPropagation();
      // long replies open in the Expand modal rather than growing inline
      this.openReplyView(reply);
    }
  }

  textOnly(note: string): string {
    if (!note) return '';
    return note
      .replace(/<img[^>]*>/gi, '')
      .replace(/<video[^>]*>.*?<\/video>/gis, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gis, '')
      .replace(/<audio[^>]*>.*?<\/audio>/gis, '')
      .replace(/<svg[^>]*>.*?<\/svg>/gis, '')
      .replace(/<figure[^>]*>.*?<\/figure>/gis, '')
      .replace(/<picture[^>]*>.*?<\/picture>/gis, '')
      .replace(/<object[^>]*>.*?<\/object>/gis, '')
      .replace(/<embed[^>]*>/gi, '')
      .trim();
  }

  reactToNote(note, reactionType) {
    const noteType: 'note' | 'reply' = note?.projectName ? 'note' : 'reply';
    const reactionExists = note.reactions.find(r => r.type === reactionType);
    const rollback = JSON.parse(JSON.stringify(note.reactions));

    if (reactionExists?.reactedByMe) {
      reactionExists.reactedByMe = false;
      if (reactionExists.reactedBy.length === 0) {
        note.reactions = note.reactions.filter(r => r.type !== reactionType);
      }
      this.noteService.removeNoteReaction(noteType, note.id, reactionType).subscribe({
        error: () => { this.alertify.error('Could not save note reaction'); note.reactions = rollback; }
      });
    } else {
      if (reactionExists) { reactionExists.reactedByMe = true; }
      else { note.reactions.push({ type: reactionType, reactedByMe: true, reactedBy: [] }); }
      this.noteService.addNoteReaction(noteType, note.id, reactionType).subscribe({
        error: () => { this.alertify.error('Could not save note reaction'); note.reactions = rollback; }
      });
    }
  }

  showDelete(note) {
    this.deleteNote = note;
    this.openModal(this.modalDelete);
  }

  openModal(template: any, classes = 'modal-sm nav-modal nav-modal-style nav-sidebar-modal') {
    this.openModalRef = this.modalService.show(template, { ignoreBackdropClick: true, keyboard: false, class: classes });
  }

  closeModal() { this.openModalRef.hide(); this.openModalRef = null; }

  // ── Expand-to-modal (compose / edit / view for notes and replies) ─────────

  private showNoteModal() {
    this.openModal(this.modalNote, 'modal-lg nav-modal nav-modal-style');
  }

  openComposeModal() {
    this.addNoteModeOn = true;
    this.noteModal = { editorMode: 'compose', readOnly: false, target: null, isReply: false, canEdit: false, title: 'Add note' };
    this.showNoteModal();
  }

  openNoteView(note: any) {
    this.noteModal = { editorMode: null, readOnly: true, target: note, isReply: false, canEdit: this.auth.getUser().id === note.userId, title: 'Note' };
    this.showNoteModal();
  }

  openReplyView(reply: any) {
    this.noteModal = { editorMode: null, readOnly: true, target: reply, isReply: true, canEdit: this.auth.getUser().id === reply.createdBy, title: 'Reply' };
    this.showNoteModal();
  }

  openNoteEdit(note: any) {
    if (!this.notesConfirmEdits[note.id]?.editModeOn) this.editNote(note);
    this.noteModal = { editorMode: 'editNote', readOnly: false, target: note, isReply: false, canEdit: true, title: 'Edit note' };
    this.showNoteModal();
  }

  openReplyEdit(reply: any) {
    if (!this.notesConfirmEdits[reply.id]?.editModeOn) this.notesConfirmEdits[reply.id] = { editModeOn: true, message: reply.message };
    this.noteModal = { editorMode: 'editReply', readOnly: false, target: reply, isReply: true, canEdit: true, title: 'Edit reply' };
    this.showNoteModal();
  }

  openAddReplyModal(note: any) {
    this.replyToNote(note);
    this.noteModal = { editorMode: 'addReply', readOnly: false, target: note, isReply: true, canEdit: false, title: 'Reply' };
    this.showNoteModal();
  }

  // Switch an open read-only modal into edit mode, reusing the inline edit setup
  editFromView() {
    if (!this.noteModal?.target) return;
    const target = this.noteModal.target;
    if (this.noteModal.isReply) {
      if (!this.notesConfirmEdits[target.id]?.editModeOn) this.notesConfirmEdits[target.id] = { editModeOn: true, message: target.message };
      this.noteModal = { ...this.noteModal, editorMode: 'editReply', readOnly: false, title: 'Edit reply' };
    } else {
      if (!this.notesConfirmEdits[target.id]?.editModeOn) this.editNote(target);
      this.noteModal = { ...this.noteModal, editorMode: 'editNote', readOnly: false, title: 'Edit note' };
    }
  }

  // Editor model accessors — the modal edits the same state as the inline boxes
  get modalEditorData(): string {
    if (!this.noteModal) return '';
    if (this.noteModal.editorMode === 'compose') return this.newNote?.message ?? '';
    if (!this.noteModal.target) return '';
    const state = this.notesConfirmEdits[this.noteModal.target.id];
    if (this.noteModal.editorMode === 'addReply') return state?.replyMessage ?? '';
    return state?.message ?? '';
  }

  setModalEditorMessage(message: string) {
    if (!this.noteModal) return;
    if (this.noteModal.editorMode === 'compose') { this.newNote.message = message; return; }
    if (!this.noteModal.target) return;
    const id = this.noteModal.target.id;
    if (!this.notesConfirmEdits[id]) this.notesConfirmEdits[id] = {};
    if (this.noteModal.editorMode === 'addReply') this.notesConfirmEdits[id].replyMessage = message;
    else this.notesConfirmEdits[id].message = message;
  }

  // Draft key of the note the modal composes or edits; null for replies, which can't carry attachments
  get modalAttachmentDraftKey(): string | null {
    if (this.noteModal?.editorMode === 'compose') return NEW_NOTE_DRAFT;
    if (this.noteModal?.editorMode === 'editNote') return this.noteModal.target?.id ?? null;
    return null;
  }

  get modalEditorConfig() {
    return (this.noteModal?.editorMode === 'editReply' || this.noteModal?.editorMode === 'addReply')
      ? this.editorReplyConfig
      : this.editorConfig;
  }

  saveNoteModal() {
    const modal = this.noteModal;
    if (!modal) { this.closeNoteModal(); return; }
    // keep the modal open so the uploads stay visible until they finish
    const draftKey = this.modalAttachmentDraftKey;
    if (draftKey && this.isUploadingAttachments(draftKey)) {
      this.alertify.error('Please wait for your attachments to finish uploading.');
      return;
    }
    switch (modal.editorMode) {
      case 'compose': this.addNote(); break;
      case 'editNote': if (modal.target) this.updateNote(modal.target); break;
      case 'editReply': if (modal.target) this.editReply(modal.target); break;
      case 'addReply': if (modal.target) this.saveReply(modal.target); break;
    }
    this.closeNoteModal();
  }

  // Cancel only dismisses the modal — the inline editor it expanded from
  // (add note, edit note, add/edit reply) stays open; they share the same
  // model state, so nothing typed is lost
  cancelNoteModal() {
    this.closeNoteModal();
  }

  closeNoteModal() {
    this.closeModal();
    // clear the state only once the fade-out has finished — nulling it right
    // away flips the template to the editor branch (readOnly turns falsy)
    // mid-animation, visibly growing the modal before it disappears
    this.modalService.onHidden.pipe(take(1)).subscribe(() => this.noteModal = null);
  }

  close() { this.closeCallback.emit({ action: 'close' }); }

  // ── Note attachments ──────────────────────────────────────────────────────

  attachmentsFor(draftKey: string): NoteAttachment[] {
    return this.draftAttachments[draftKey] ?? [];
  }

  uploadsFor(draftKey: string): AttachmentUpload[] {
    return this.attachmentUploads[draftKey] ?? [];
  }

  isUploadingAttachments(draftKey: string): boolean {
    return this.uploadsFor(draftKey).length > 0;
  }

  onAttachmentFileSelected(draftKey: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (file.size > MAX_ATTACHMENT_BYTES) {
      this.alertify.error(`${file.name} is larger than the 500 MB limit.`);
      return;
    }

    const upload: AttachmentUpload = { id: uuidv4(), fileName: file.name, progress: 0 };
    this.attachmentUploads[draftKey] = [...this.uploadsFor(draftKey), upload];

    // S3 keys keep to safe characters; the original name is what the note and Project Folder show
    const s3FileName = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
    let fileUrl = '';

    upload.subscription = this.uploader.generatePresignedUrl('', `${NOTE_ATTACHMENT_FOLDER}/${upload.id}`, false, s3FileName).pipe(
      switchMap((response: PresignedUpload) => {
        fileUrl = response.url;
        return this.uploader.uploadFileToS3(file, response.uploadUrl);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: uploadEvent => {
        if (uploadEvent.type === HttpEventType.UploadProgress && uploadEvent.total) {
          upload.progress = Math.round((uploadEvent.loaded / uploadEvent.total) * 100);
        }
      },
      error: () => {
        this.removeUpload(draftKey, upload.id);
        this.alertify.error(`Unable to upload ${file.name}.`);
      },
      complete: () => {
        this.removeUpload(draftKey, upload.id);
        this.draftAttachments[draftKey] = [...this.attachmentsFor(draftKey), { fileName: file.name, url: fileUrl, assetType: 'file' }];
      }
    });
  }

  openLinkForm(draftKey: string): void {
    this.linkDrafts[draftKey] = { name: '', url: '' };
  }

  closeLinkForm(draftKey: string): void {
    delete this.linkDrafts[draftKey];
  }

  addLinkAttachment(draftKey: string): void {
    const draft = this.linkDrafts[draftKey];
    const rawUrl = draft?.url?.trim() ?? '';
    // accept "www.example.com" by assuming https
    const url = /^[a-z][a-z0-9+.-]*:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;

    if (!rawUrl || !this.isWebLink(url)) {
      this.alertify.error('Please enter a valid link.');
      return;
    }

    const fileName = draft.name?.trim() || url;
    this.draftAttachments[draftKey] = [...this.attachmentsFor(draftKey), { fileName, url, assetType: 'link' }];
    this.closeLinkForm(draftKey);
  }

  removeDraftAttachment(draftKey: string, index: number): void {
    this.draftAttachments[draftKey] = this.attachmentsFor(draftKey).filter((_, i) => i !== index);
  }

  keptAttachmentsFor(draftKey: string): NoteAttachment[] {
    return this.keptAttachments[draftKey] ?? [];
  }

  // A saved attachment is only dropped from the note when the edit is submitted
  removeKeptAttachment(draftKey: string, attachmentId: string): void {
    this.keptAttachments[draftKey] = this.keptAttachmentsFor(draftKey).filter(a => a.id !== attachmentId);
  }

  downloadAttachment(noteId: string, attachment: NoteAttachment): void {
    this.noteService.getAttachmentDownloadUrl(noteId, attachment.id).pipe(takeUntil(this.destroy$)).subscribe({
      // the presigned URL is served as an attachment, so following it downloads the file
      next: url => {
        const link = document.createElement('a');
        link.href = url;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
      },
      error: () => this.alertify.error('Unable to download file')
    });
  }

  private isWebLink(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private removeUpload(draftKey: string, uploadId: string): void {
    this.attachmentUploads[draftKey] = this.uploadsFor(draftKey).filter(u => u.id !== uploadId);
  }

  private clearDraftAttachments(draftKey: string): void {
    this.uploadsFor(draftKey).forEach(u => u.subscription?.unsubscribe());
    delete this.attachmentUploads[draftKey];
    delete this.draftAttachments[draftKey];
    delete this.keptAttachments[draftKey];
    delete this.linkDrafts[draftKey];
  }

  private resetNewNote() {
    this.showContactPicker = false;
    this.showAddContactForm = false;
    this.selectedPickerContact = null;
    this.newPickerContact = {};
    this.newNote = {
      userType: this.clientId ? 'Client' : this.vendorId ? 'Vendor' : 'Team',
      clientId: this.clientId,
      partnerId: this.vendorId
    };
  }

  private extractTags(message: string): Tags[] {
    const matches = message?.match(/\B#\w\w+\b/g) || [];
    return matches.map(tag => ({ id: uuidv4(), tag: tag.replace(/#/g, '') } as Tags));
  }

  // ── Inline contact picker ─────────────────────────────────────────────────

  get filteredPickerContacts(): any[] {
    if (!this.contactPickerSearch) return this.pickerContacts;
    const s = this.contactPickerSearch.toLowerCase();
    return this.pickerContacts.filter(c =>
      c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s)
    );
  }

  get pickerTitle(): string {
    if (this.contactPickerMode === 'client') return this.resolvedClientName || 'this client';
    return this.vendors.find(v => v.id === this.pickerVendorId)?.name || 'a vendor';
  }

  openContactPicker(mode: 'client' | 'vendor') {
    this.showContactPicker = true;
    this.contactPickerMode = mode;
    this.contactPickerSearch = '';
    this.selectedPickerContact = null;
    this.showAddContactForm = false;
    this.newPickerContact = {};
    this.pickerVendorId = null;

    if (mode === 'client') {
      this.pickerContacts = this.clientContacts;
    } else {
      this.refreshVendorPickerContacts();
    }
  }

  onPickerVendorChange() {
    this.contactPickerSearch = '';
    this.selectedPickerContact = null;
    this.refreshVendorPickerContacts();
  }

  initAddContactForm() {
    this.newPickerContact = { vendorId: this.contactPickerMode === 'vendor' ? this.pickerVendorId : null };
    this.showAddContactForm = true;
  }

  // Lists contacts for the one vendor chosen in the picker; auto-picks it when only one vendor is missing a contact
  private refreshVendorPickerContacts() {
    const missing = this.vendorsMissingContacts;
    if (!missing.some(v => v.id === this.pickerVendorId)) {
      this.pickerVendorId = missing.length === 1 ? missing[0].id : null;
    }
    const vendor = this.vendors.find(v => v.id === this.pickerVendorId);
    this.pickerContacts = vendor?.contacts || [];
  }

  cancelContactPicker() {
    this.showContactPicker = false;
    this.selectedPickerContact = null;
    this.showAddContactForm = false;
    this.newPickerContact = {};
  }

  confirmContactSelection() {
    if (!this.selectedPickerContact || this.contactPickerSaving) return;
    this.contactPickerSaving = true;

    if (this.contactPickerMode === 'client') {
      this.project.clientContactId = this.selectedPickerContact.id;
      this.projectService.UpdateProjectValue(this.project.id, 'clientContactId', this.selectedPickerContact.id).subscribe({
        next: () => {
          this.alertify.success('Point of contact updated');
          this.showContactPicker = false;
          this.contactPickerSaving = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.project.clientContactId = null;
          this.alertify.error('Unable to update contact');
          this.contactPickerSaving = false;
        }
      });
    } else {
      this.applyVendorPm(this.selectedPickerContact);
    }
  }

  saveNewPickerContact() {
    if (!this.newPickerContact.firstName?.trim() || !this.newPickerContact.lastName?.trim() || !this.newPickerContact.email?.trim()) {
      this.alertify.error('First name, last name, and email are required.');
      return;
    }
    if (this.contactPickerMode === 'vendor' && !this.newPickerContact.vendorId) {
      this.alertify.error('Please select a vendor for this contact.');
      return;
    }
    this.contactPickerSaving = true;

    if (this.contactPickerMode === 'client') {
      const contact = {
        id: uuidv4(), mode: 'new',
        firstName: this.newPickerContact.firstName.trim(),
        lastName: this.newPickerContact.lastName.trim(),
        email: this.newPickerContact.email.trim(),
        clientId: this.project.clientId,
        phone: '', billing: false
      };
      this.clientContactService.AddClientContact(contact).subscribe({
        next: () => {
          const mapped = { id: contact.id, name: [contact.firstName, contact.lastName].filter(Boolean).join(' '), type: 'ClientContact', email: contact.email };
          this.clientContacts = [...this.clientContacts, mapped];
          this.project.clientContactId = contact.id;
          this.projectService.UpdateProjectValue(this.project.id, 'clientContactId', contact.id).subscribe({
            next: () => {
              this.alertify.success('Contact added and set as point of contact');
              this.showContactPicker = false;
              this.contactPickerSaving = false;
              this.cdr.detectChanges();
            },
            error: () => { this.alertify.error('Contact saved but could not update project'); this.contactPickerSaving = false; }
          });
        },
        error: (err) => {
          const body = err?.error;
          if (typeof body === 'string' && body.length > 0 && body !== 'EMAIL_EXISTS') {
            this.alertify.error(body);
          } else {
            this.alertify.error('Unable to add contact. This email may already be in use.');
          }
          this.contactPickerSaving = false;
        }
      });
    } else {
      const targetVendorId = this.newPickerContact.vendorId;
      const emailLower = this.newPickerContact.email.trim().toLowerCase();
      const duplicate = this.vendorsContacts.find(c => c.email?.toLowerCase() === emailLower);
      if (duplicate) {
        this.alertify.error('This email address is already in use for a vendor contact.');
        return;
      }
      const contact = {
        id: uuidv4(), mode: 'new',
        firstName: this.newPickerContact.firstName.trim(),
        lastName: this.newPickerContact.lastName.trim(),
        email: this.newPickerContact.email.trim(),
        partnerId: targetVendorId,
        phone: '', role: 'Vendor', billing: false
      };
      this.partnerContactService.AddPartnerContact(contact).subscribe({
        next: () => {
          const mapped = { id: contact.id, name: [contact.firstName, contact.lastName].filter(Boolean).join(' '), type: 'VendorContact', vendorId: targetVendorId, email: contact.email };
          const vendor = this.vendors.find(v => v.id === targetVendorId);
          if (vendor) vendor.contacts = [...(vendor.contacts || []), mapped];
          this.vendorsContacts = [...this.vendorsContacts, mapped];
          this.applyVendorPm(mapped);
        },
        error: (err) => {
          const body = err?.error;
          if (typeof body === 'string' && body.length > 0 && body !== 'EMAIL_EXISTS') {
            this.alertify.error(body);
          } else {
            this.alertify.error('Unable to add contact. This email may already be in use.');
          }
          this.contactPickerSaving = false;
        }
      });
    }
  }

  private applyVendorPm(contact: any) {
    const vendorId = contact.vendorId;
    const vendorName = this.vendors.find(v => v.id === vendorId)?.name || '';
    const newEntry: any = { userId: contact.id, displayName: contact.name, projectRole: 'Vendor PM', partnerId: vendorId };
    const updatedTeam = [...(this.project.projectTeam || []), newEntry];
    this.projectService.UpdateProjectTeam(this.project.id, updatedTeam).subscribe({
      next: () => {
        this.project.projectTeam = updatedTeam;
        this.alertify.success(`Contact saved for vendor ${vendorName}`);
        this.contactPickerSaving = false;
        if (this.anyVendorHasNoContacts()) {
          // Keep the picker open for the vendors still missing a contact
          this.selectedPickerContact = null;
          this.showAddContactForm = false;
          this.newPickerContact = {};
          this.refreshVendorPickerContacts();
        } else {
          this.showContactPicker = false;
        }
        this.cdr.detectChanges();
      },
      error: () => { this.alertify.error('Unable to update vendor contact'); this.contactPickerSaving = false; }
    });
  }
}
